import React from 'react';
import { X } from 'lucide-react';

interface LedInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PowerIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M12 2v6M5.27 5.27A9 9 0 1 0 12 3" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

const WifiIcon = () => (
  <svg width="16" height="12" viewBox="0 0 24 17" fill="none">
    <path d="M12 17L0 5C1.58 3.38 3.41 2.15 5.49 1.29C7.56 0.43 9.73 0 12 0C14.27 0 16.44 0.43 18.51 1.29C20.59 2.15 22.42 3.38 24 5L12 17Z" fill="white" />
  </svg>
);

const AqiIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M2 12h2l3-8 4 16 3-10 2 2h6" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LED_INFO = [
  {
    name: 'Power LED',
    icon: PowerIcon,
    states: [
      { label: 'Solid Green', dotColor: '#13AA08', blink: false, description: 'Device is powered on and running normally.' },
      { label: 'Off', dotColor: '#9AA5B1', blink: false, description: 'Device is not receiving power. Check the power cable.' },
    ],
  },
  {
    name: 'WiFi LED',
    icon: WifiIcon,
    states: [
      { label: 'Solid Green', dotColor: '#13AA08', blink: false, description: 'Connected to WiFi with internet available.' },
      { label: 'Blinking Blue', dotColor: '#3B82F6', blink: true, description: 'If it blinks for 3sec, device is sending data. If it blinks for more than 3sec, device is getting updated.' },
      { label: 'Solid Red', dotColor: '#D2042D', blink: false, description: 'Device is not connected to any WiFi network.' },
      { label: 'Solid Yellow', dotColor: '#EEC94D', blink: false, description: 'Device is connected to WiFi but no internet available.' },
    ],
  },
  {
    name: 'AQI LED',
    icon: AqiIcon,
    states: [
      { label: 'Solid Green', dotColor: '#13AA08', blink: false, description: 'Air quality is safe and healthy.' },
      { label: 'Solid Yellow', dotColor: '#EEC94D', blink: false, description: 'Air quality is acceptable but caution advised.' },
      { label: 'Solid Red', dotColor: '#D2042D', blink: false, description: 'Air quality is unhealthy. Take necessary precautions.' },
    ],
  },
];

const LedInfoModal: React.FC<LedInfoModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="dcf-led-page">
      <div className="dcf-led-page__header">
        <h3 className="dcf-led-page__title">SmartEco LED Indicator</h3>
        <button
          type="button"
          className="dcf-led-page__close"
          onClick={onClose}
          aria-label="Close"
        >
          <X size={22} />
        </button>
      </div>

      <div className="dcf-led-page__body">
        {LED_INFO.map((led) => (
          <div key={led.name} className="dcf-led-card">
            <div className="dcf-led-card__header">
              <span className="dcf-led-card__icon">
                <led.icon />
              </span>
              <span className="dcf-led-card__name">{led.name}</span>
            </div>
            <div className="dcf-led-card__states">
              {led.states.map((state) => (
                <div key={state.label} className="dcf-led-state">
                  <span className="dcf-led-state__label">
                    <span
                      className={`dcf-led-dot--small${state.blink ? ' dcf-led-blink' : ''}`}
                      style={{ backgroundColor: state.dotColor, boxShadow: `0 0 7px ${state.dotColor}99` }}
                    />
                    {state.label}
                  </span>
                  <span className="dcf-led-state__desc">{state.description}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LedInfoModal;
